import { useState, useCallback } from 'react';
import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ItemListSchema, SpeakableSchema } from '@/components/seo/seo';
import { clsx } from 'clsx';
import { councilPages, getCouncilDisplayName } from '@/lib/council-pages';
import {
  STATIC_AUTHORITY_LEAGUE_TABLE,
  type AuthorityLeagueTable,
  type AuthorityRating,
} from '@/data/authority-league-table';

function RatingBadge({ rating, compact = false }: { rating: 'green' | 'amber' | 'red' | null; compact?: boolean }) {
  if (!rating) {
    return (
      <span className={clsx(
        'inline-flex items-center justify-center rounded-full border font-semibold',
        compact ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-xs',
        'bg-surface-100 text-surface-500'
      )}>
        Not rated
      </span>
    );
  }

  const styles = {
    green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-rose-50 text-rose-800 border-rose-200',
  };

  const labels = {
    green: 'Green',
    amber: 'Amber',
    red: 'RED',
  };

  return (
    <span className={clsx(
      'inline-flex items-center justify-center rounded-full border font-bold tracking-wide',
      compact ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-xs',
      styles[rating]
    )}>
      {labels[rating]}
    </span>
  );
}

// Share button component
function ShareButton({ 
  platform, 
  url, 
  text, 
  className 
}: { 
  platform: 'facebook' | 'twitter' | 'copy'; 
  url: string; 
  text: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);

    switch (platform) {
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
          '_blank',
          'width=600,height=400'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(`${text} ${url}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
    }
  }, [platform, url, text]);

  const icons = {
    facebook: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    copy: copied ? (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
      </svg>
    ),
  };

  const labels = {
    facebook: 'Facebook',
    twitter: 'X',
    copy: copied ? 'Copied' : 'Copy link',
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label={labels[platform]}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
        platform === 'facebook' && 'bg-white text-[#1877F2] border-[#1877F2]/20 hover:bg-[#1877F2] hover:text-white',
        platform === 'twitter' && 'bg-white text-surface-900 border-surface-300 hover:bg-surface-900 hover:text-white',
        platform === 'copy' && 'bg-white text-brand-700 border-brand-200 hover:bg-brand-50',
        className
      )}
    >
      {icons[platform]}
      {labels[platform]}
    </button>
  );
}

// Council card for the shame list
function ShameCard({ 
  council, 
  position,
  hasPage 
}: { 
  council: AuthorityRating; 
  position: number;
  hasPage: string | undefined;
}) {
  const shareUrl = hasPage 
    ? `https://fixtyer.co.uk${hasPage}` 
    : 'https://fixtyer.co.uk/pothole-hall-of-shame';
  const displayName = getCouncilDisplayName(council.name);
  const shareText = `${displayName} is RED-rated in the DfT road maintenance data. See the Fixtyer council breakdown:`;

  const scorecards = [
    { label: 'Overall', rating: council.dftOverallRating },
    { label: 'Condition', rating: council.dftConditionRating },
    { label: 'Spend', rating: council.dftSpendRating },
    { label: 'Best practice', rating: council.dftBestPracticeRating },
  ];

  const redCount = scorecards
    .map((scorecard) => scorecard.rating)
    .filter(r => r === 'red').length;
  const redLabels = scorecards
    .filter((scorecard) => scorecard.rating === 'red')
    .map((scorecard) => scorecard.label);

  return (
    <article className="group flex h-full flex-col rounded-2xl border border-surface-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-rose-200 hover:shadow-lg">
      <div className="flex items-start justify-between gap-4 border-b border-surface-100 px-5 py-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-600">
            Rank {position}
          </p>
          {hasPage ? (
            <Link 
              to={hasPage} 
              className="mt-1 flex items-center gap-2 font-display text-xl font-bold text-brand-950 transition-colors hover:text-rose-700"
            >
              {displayName}
              <svg className="h-4 w-4 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          ) : (
            <h3 className="mt-1 font-display text-xl font-bold text-brand-950">{displayName}</h3>
          )}
          <p className="mt-1 text-sm text-surface-500">{council.region}</p>
        </div>

        <div className="rounded-xl bg-rose-50 px-3 py-2 text-center">
          <div className="text-2xl font-black text-rose-700">{redCount}</div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">red</div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <p className="mb-4 text-sm text-surface-700">
          RED on {redLabels.length ? redLabels.join(', ') : 'the DfT scorecard'} in the latest road maintenance ratings.
        </p>

        <div className="grid grid-cols-2 gap-2">
          {scorecards.map((scorecard) => (
            <div key={scorecard.label} className="rounded-xl border border-surface-200 bg-surface-50 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-surface-500">
                {scorecard.label}
              </div>
              <RatingBadge rating={scorecard.rating} compact />
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-surface-100 pt-4">
          {hasPage && (
            <Link
              to={hasPage}
              className="inline-flex flex-1 items-center justify-center rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
            >
              View evidence
            </Link>
          )}
          <ShareButton 
            platform="facebook" 
            url={shareUrl} 
            text={shareText}
            className="flex-1"
          />
          <ShareButton 
            platform="twitter" 
            url={shareUrl} 
            text={shareText}
            className="flex-1"
          />
          <div className="basis-full">
            <ShareButton 
              platform="copy" 
              url={shareUrl} 
              text={shareText}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </article>
  );
}

export function PotholeHallOfShamePage() {
  const [data] = useState<AuthorityLeagueTable>(STATIC_AUTHORITY_LEAGUE_TABLE);

  // Filter for RED-rated councils (overall only)
  const shameList = data?.authorities
    .filter(auth => auth.dftOverallRating === 'red')
    .sort((a, b) => {
      // Sort by number of red ratings (most severe first)
      const aRedCount = [a.dftOverallRating, a.dftSpendRating, a.dftConditionRating, a.dftBestPracticeRating]
        .filter(r => r === 'red').length;
      const bRedCount = [b.dftOverallRating, b.dftSpendRating, b.dftConditionRating, b.dftBestPracticeRating]
        .filter(r => r === 'red').length;
      
      // Sort by red count descending, then alphabetically
      if (bRedCount !== aRedCount) return bRedCount - aRedCount;
      return a.name.localeCompare(b.name);
    }) || [];

  // Generate ItemList items for schema
  const itemListItems = shameList.map((council, index) => ({
    name: getCouncilDisplayName(council.name),
    url: councilPages[council.name] || '/pothole-hall-of-shame',
    position: index + 1,
    description: `${getCouncilDisplayName(council.name)} - DfT rated ${council.dftOverallRating?.toUpperCase() || 'N/A'} overall, ${council.dftSpendRating?.toUpperCase() || 'N/A'} for spend`,
  }));

  const pageShareUrl = 'https://fixtyer.co.uk/pothole-hall-of-shame';
  const pageShareText = 'Is your council RED-rated for road maintenance? See Fixtyer\'s Pothole Hall of Shame, based on Department for Transport data:';
  const displayedRedCount = data?.stats.red ?? 13;
  const totalCouncils = data?.stats.total ?? 154;
  const ratingYear = data?.ratingYear || '2025-2026';

  const faqItems = [
    {
      question: 'What qualifies a council for the Hall of Shame?',
      answer: 'Councils appear on this list if they received a RED overall rating from the Department for Transport in the latest local road maintenance ratings.',
    },
    {
      question: 'How does a RED rating affect my pothole damage claim?',
      answer: 'A RED rating does not prove an individual claim by itself, but it can be useful context. Your claim still depends on the specific pothole, the council\'s inspection records, previous reports, repair history, and the evidence you provide.',
    },
    {
      question: 'Where does this data come from?',
      answer: 'The scorecards come from the Department for Transport Local Road Maintenance Ratings 2025-2026, published on GOV.UK and based on information submitted by local highway authorities.',
    },
    {
      question: 'Is this list updated automatically?',
      answer: 'The list is generated from Fixtyer\'s authority ratings data. When that dataset is refreshed after a new DfT release, councils can move in or out of the list.',
    },
    {
      question: 'Why should I share this page?',
      answer: 'Sharing a council card can help other local drivers find the public DfT rating, see the relevant council evidence page, and understand what information may matter when preparing a pothole damage claim.',
    },
  ];

  return (
    <div className="min-h-screen bg-surface-50">
      <SEO
        title="13 RED-Rated English Road Authorities 2025-2026 | Fixtyer"
        description="See the 13 of 154 English highway authorities in the DfT RED overall road maintenance band for 2025-2026. Network context only, not proof of notice or liability."
        canonical="/pothole-hall-of-shame"
        image="https://fixtyer.co.uk/og-hall-of-shame.png"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Council Ratings', url: '/authority-ratings' },
          { name: 'Hall of Shame', url: '/pothole-hall-of-shame' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      {shameList.length > 0 && (
        <ItemListSchema
          name="England Pothole Hall of Shame 2026"
          description="English highway authorities in the RED Department for Transport network-level road maintenance rating band"
          url="/pothole-hall-of-shame"
          items={itemListItems}
        />
      )}
      <SpeakableSchema
        url="/pothole-hall-of-shame"
        headline="England Pothole Hall of Shame 2026"
        cssSelectors={['[data-speakable="summary"]', 'h1']}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-rose-50">
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <pattern id="hall-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hall-grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 lg:pt-24 lg:pb-24">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 text-sm text-brand-600 mb-6">
                <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
                <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <Link to="/authority-ratings" className="hover:text-brand-700 transition-colors">Council Ratings</Link>
                <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-surface-800">Hall of Shame</span>
              </div>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full text-rose-700 text-sm font-semibold mb-6">
                DfT RED-rated councils - {ratingYear}
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight mb-6">
                13 English Authorities in the DfT RED Rating Band
              </h1>

              <p 
                className="text-lg sm:text-xl text-surface-700 max-w-3xl leading-relaxed mb-8"
                data-speakable="summary"
              >
                13 of 154 rated English highway authorities are in the DfT RED overall band for 2025-2026.
                The other 125 are AMBER and 16 are GREEN. These network-level ratings do not establish notice,
                breach or liability for a particular defect.
              </p>
              <p className="mb-6 text-sm font-medium text-surface-600">Last reviewed: 19 July 2026</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="#council-list"
                  className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white rounded-xl font-semibold hover:bg-brand-700 transition-colors"
                >
                  See RED Councils
                </a>
                <Link
                  to="/road-maintenance-report"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-700 rounded-xl font-semibold border border-brand-200 hover:bg-brand-50 transition-colors"
                >
                  View Full DfT Table
                </Link>
                <Link
                  to="/councils"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-700 rounded-xl font-semibold border border-brand-200 hover:bg-brand-50 transition-colors"
                >
                  Find Your Council
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-surface-200 shadow-xl shadow-brand-950/5 p-6">
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="rounded-2xl bg-rose-50 p-4 text-center">
                  <div className="text-3xl font-black text-rose-700">{displayedRedCount}</div>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-rose-700">RED councils</div>
                </div>
                <div className="rounded-2xl bg-brand-50 p-4 text-center">
                  <div className="text-3xl font-black text-brand-700">{totalCouncils}</div>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-700">rated</div>
                </div>
                <div className="rounded-2xl bg-amber-50 p-4 text-center">
                  <div className="text-3xl font-black text-amber-700">4</div>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-amber-700">scorecards</div>
                </div>
              </div>

              <div className="rounded-2xl bg-surface-50 border border-surface-200 p-5">
                <p className="font-display text-lg font-semibold text-brand-950 mb-2">
                  Share the national list
                </p>
                <p className="text-sm text-surface-600 mb-4">
                  Use this page to show the full RED-rated group, or share an individual council card below.
                </p>
                <div className="grid sm:grid-cols-3 gap-2">
                  <ShareButton 
                    platform="facebook" 
                    url={pageShareUrl} 
                    text={pageShareText}
                  />
                  <ShareButton 
                    platform="twitter" 
                    url={pageShareUrl} 
                    text={pageShareText}
                  />
                  <ShareButton 
                    platform="copy" 
                    url={pageShareUrl} 
                    text={pageShareText}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What RED Means */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="md:col-span-2 rounded-3xl border border-surface-200 bg-surface-50 p-8">
              <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
                What a RED rating means
              </h2>
              <div className="space-y-4 text-surface-700 leading-relaxed">
                <p>
                  The Department for Transport rates local highway authorities using published data on road condition,
                  maintenance spend, and best-practice adoption.
                </p>
                <p>
                  A RED overall rating is the lower DfT network-level band. It is not a finding about a specific defect
                  or a shortcut to compensation, but it can provide context when deciding which inspection, report and
                  repair records may matter.
                </p>
              </div>
            </div>
            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8">
              <h3 className="font-display text-xl font-bold text-amber-950 mb-3">
                Claim context
              </h3>
              <p className="text-amber-900 leading-relaxed">
                Individual pothole claims still depend on the defect, photos, repair costs, inspection records, prior
                reports, and the council's Section 58 evidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Shame List */}
      <section id="council-list" className="py-16 bg-surface-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-rose-600 mb-3">
                Shareable council cards
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mb-3">
                {displayedRedCount} RED-rated councils
              </h2>
              <p className="text-surface-600 max-w-2xl">
                Click through for a council-specific evidence page, or share a compact card with your local group.
              </p>
            </div>
            <Link
              to="/authority-ratings"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-brand-200 bg-white px-5 py-3 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50"
            >
              Search all {totalCouncils} ratings
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {shameList.map((council, index) => (
              <ShameCard
                key={council.id}
                council={council}
                position={index + 1}
                hasPage={councilPages[council.name]}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold text-brand-950 mb-3">
              Frequently asked questions
            </h2>
            <p className="text-surface-600">
              How to read, use, and share the RED-rated council list.
            </p>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-surface-200 bg-surface-50 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-white transition-colors">
                  <h3 className="font-semibold text-brand-950 pr-8">{faq.question}</h3>
                  <svg 
                    className="w-5 h-5 text-brand-600 flex-shrink-0 transition-transform group-open:rotate-180" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-surface-700 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
            Need to prepare a pothole damage claim?
          </h2>
          <p className="text-lg text-brand-100 mb-8 max-w-2xl mx-auto">
            Fixtyer helps you organise your evidence and prepare your own council claim pack. The DfT rating is context;
            your photos, repair costs, location evidence, and council records still do the heavy lifting.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-bold rounded-xl hover:bg-brand-50 transition-colors text-lg"
            >
              Check Your Eligibility
            </Link>
            <Link
              to="/guides/what-makes-a-valid-claim"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-bold rounded-xl hover:bg-white/15 transition-colors border border-white/20 text-lg"
            >
              Read Claim Guidance
            </Link>
          </div>
        </div>
      </section>

      {/* Source footer */}
      <section className="py-8 bg-white border-t border-surface-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-surface-600">
            Data source: Department for Transport Local Road Maintenance Ratings {ratingYear}.{' '}
            <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:text-brand-700 underline"
            >
              View official publication
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
